// UndoBadgeSeen — Classic/Modern Undo ボタンの「New!」バッジを「初回だけ」出すための
// seen フラグ永続化 (infrastructure 層).
//
// 背景 (undo-conflict-aware-2026-07-12.md、2026-07-23 追補「New! バッジ」):
//   - conflict-aware Undo (β Phase 10) は Classic/Modern 両方に常設ボタンを持つ
//     (Classic 上部バー「取り消し」/ Modern コマンドクラスタ「元に戻す (Undo)」)。
//     新機能訴求として初回のみ「New!」バッジを出し、**初回に Undo を実行したら消す**
//     (以後は非表示)。
//   - 「実行した」= `useConflictAwareUndo.triggerUndo()` が実際に `stroke.cancel` を
//     送信した時点 (ボタン押下 / Ctrl+Z ショートカットのどちらの経路でも同じ関数を
//     通る)。**サーバに拒否された場合 (conflict / too-old 等) でも seen 化する**
//     (機能の存在は押した時点で認知済みのため、拒否はサーバ側の判定結果に過ぎない)。
//   - 絵ログ共有 / 過去キャンバスの seen store (`timelapseShareSeen.ts` /
//     `pastCanvasSeen.ts`) と**そっくり複製**し、別 localStorage key で独立管理する。
//     Classic / Modern 両方が同じ key を参照するので、どちらで Undo を実行しても
//     両方のバッジが消える (= レイアウトは見せ方の差でしかない、という設計原則)。
//
// 設計 (`timelapseShareSeen.ts` / `pastCanvasSeen.ts` と同流儀):
//   - boolean を文字列 `'1'` で保存 (= 在れば seen 済)。不在 / storage 不可は「未 seen」
//     (= まだ出す) 扱い。例外を throw しない。
//   - StorageLike 抽象 + sentinel probe。test で storage mock 注入可能。
//
// Tests: undoBadgeSeen.test.ts (storage-injection style)。

/** localStorage キー: Undo ボタンの New! バッジを消したか (= 初回に Undo を実行したか). */
export const UNDO_BADGE_SEEN_KEY = 'oekaki:undo-badge-seen';

/** Minimal Storage shape this module uses (timelapseShareSeen.ts と同流儀). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface UndoBadgeSeenStore {
  /** 既に New! バッジを消したか (= 初回に Undo を実行したか). 不在 / storage 不可 → false. */
  hasSeen(): boolean;
  /** 初回に Undo を実行したことを記録 (= 以後バッジ非表示). storage 不可なら no-op. */
  markSeen(): void;
  /** key を削除. テスト / リセット用. storage 不可なら no-op. */
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:undo-badge-seen__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateUndoBadgeSeenStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= hasSeen always false, markSeen silently drops).
   */
  storage?: StorageLike | null;
}

/**
 * `UndoBadgeSeenStore` のインスタンスを構築. storage 不可でも例外を出さず、
 * 「未 seen」扱い (= 出すが永続化されないだけ) で動作を継続する.
 */
export const createUndoBadgeSeenStore = (
  opts: CreateUndoBadgeSeenStoreOptions = {},
): UndoBadgeSeenStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    hasSeen() {
      if (storage === null) return false;
      try {
        return storage.getItem(UNDO_BADGE_SEEN_KEY) !== null;
      } catch {
        return false;
      }
    },
    markSeen() {
      if (storage === null) return;
      try {
        storage.setItem(UNDO_BADGE_SEEN_KEY, '1');
      } catch {
        // QuotaExceeded / SecurityError 等 — 出し直しになるだけで害はない.
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(UNDO_BADGE_SEEN_KEY);
      } catch {
        // ignore
      }
    },
  };
};
